import { handleTokenExpiry } from './tokenExpiredNotification';
import apiClient from './axiosInstance';

class TokenMonitorService {
  constructor() {
    this.intervalId = null;
    this.warningShown = false;
    this.checkInterval = 60000; // 1분마다 체크
    this.warningMinutes = 5; // 만료 5분 전에 경고
    this._lastKnownMinutes = null;
  }

  // 서버에 인증 상태 확인 (httpOnly 쿠키 기반)
  async checkAuthStatus() {
    try {
      const res = await apiClient.get('/auth/me');
      this._networkFailCount = 0;
      return res.data?.user ? true : false;
    } catch (err) {
      // 네트워크 오류(오프라인, 타임아웃 등)는 만료로 처리하지 않음
      if (!err.response) {
        this._networkFailCount = (this._networkFailCount || 0) + 1;
        console.warn('토큰 상태 확인 네트워크 오류 (무시):', err.message);
        // 연속 5회 이상 네트워크 실패 시 만료 처리
        return this._networkFailCount < 5 ? null : false;
      }
      // 401/403 등 서버 응답이 있는 경우 만료로 처리
      return false;
    }
  }

  // 토큰 만료까지 남은 시간(분) 계산
  // httpOnly 쿠키에서는 클라이언트에서 만료 시간을 직접 읽을 수 없으므로
  // 서버 응답 기반으로 추정합니다
  getMinutesUntilExpiry() {
    return this._lastKnownMinutes;
  }

  // 토큰 만료 경고 표시
  showExpiryWarning(minutesLeft) {
    if (this.warningShown) return;
    
    const warning = document.createElement('div');
    warning.className = 'token-expiry-warning';
    warning.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: linear-gradient(135deg, #f39c12, #e67e22);
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
      z-index: 9999;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.4;
      text-align: center;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.2);
      animation: slideDown 0.3s ease-out;
    `;

    const container = document.createElement('div');
    container.style.cssText = 'display: flex; align-items: center; gap: 12px;';

    const icon = document.createElement('span');
    icon.style.fontSize = '18px';
    icon.textContent = '\u26A0\uFE0F';

    const textBlock = document.createElement('div');
    const strong = document.createElement('strong');
    strong.textContent = '세션 만료 경고';
    const br = document.createElement('br');
    const span = document.createElement('span');
    span.style.opacity = '0.9';
    span.textContent = `${minutesLeft}분 후 자동 로그아웃됩니다. 작업을 저장해주세요.`;
    textBlock.append(strong, br, span);

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '\u2715';
    closeBtn.style.cssText = 'background: none; border: none; color: white; font-size: 16px; cursor: pointer; opacity: 0.7; padding: 4px;';
    closeBtn.addEventListener('click', () => {
      warning.remove();
      this.warningShown = false;
    });

    container.append(icon, textBlock, closeBtn);
    warning.appendChild(container);

    // CSS 애니메이션 추가
    if (!document.querySelector('#token-warning-styles')) {
      const style = document.createElement('style');
      style.id = 'token-warning-styles';
      style.textContent = `
        @keyframes slideDown {
          from { 
            transform: translateX(-50%) translateY(-100%); 
            opacity: 0; 
          }
          to { 
            transform: translateX(-50%) translateY(0); 
            opacity: 1; 
          }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(warning);
    this.warningShown = true;

    // 3초 후 자동으로 경고 제거
    setTimeout(() => {
      if (warning.parentNode) {
        warning.style.animation = 'slideDown 0.3s ease-out reverse';
        setTimeout(() => {
          warning.remove();
          this.warningShown = false;
        }, 300);
      }
    }, 8000);
  }

  // 토큰 상태 체크 (서버에 인증 상태 확인)
  async checkTokenStatus() {
    const isValid = await this.checkAuthStatus();

    // null = 네트워크 오류, 다음 주기에 재시도
    if (isValid === null) return;

    if (!isValid) {
      // 토큰이 없거나 만료됨
      this.stopMonitoring();
      handleTokenExpiry();
      return;
    }
  }

  // 모니터링 시작
  startMonitoring() {
    // 기존 모니터링이 있으면 중지
    this.stopMonitoring();
    
    // 즉시 한 번 체크
    this.checkTokenStatus();
    
    // 주기적으로 체크
    this.intervalId = setInterval(() => {
      this.checkTokenStatus();
    }, this.checkInterval);
    
  }

  // 모니터링 중지
  stopMonitoring() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  // 경고 상태 초기화
  resetWarning() {
    this.warningShown = false;
  }
}

// 싱글톤 인스턴스 생성
const tokenMonitor = new TokenMonitorService();


export default tokenMonitor; 